import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Chip, CircularProgress, Select, MenuItem,
  FormControl, InputLabel, Paper, Alert,
  Menu, MenuItem as MuiMenuItem,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
import { wmsPersonnelPermissionService } from '../services/wmsPersonnelPermissionService';
import { useAuth } from '../../../contexts/AuthContext';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { screenService, WmsScreenType } from '../services/screenService';
import WmsScreenModal from './WmsScreenModal';
import WmsScreenPersonnelModal from './WmsScreenPersonnelModal';
import GuideButton from '../../common/guide/GuideButton';

interface Screen {
  id: string;
  name: string;
  warehouseId: string;
  isActive: boolean;
  settings?: string;
  createdAt: string;
  cashierCount: number;
}

interface Warehouse {
  id: string;
  name: string;
}

interface WmsScreenSetupProps {
  screenType: WmsScreenType;
  title: string;
}

const TABLE_HEADER_SX = {
  '& .MuiTableCell-root': {
    bgcolor: 'primary.main',
    color: 'common.white',
    fontWeight: 700,
    fontSize: '0.78rem',
    borderRight: '1px solid rgba(255,255,255,0.15)',
    py: 1,
    px: 1.5,
    '&:last-of-type': {
      borderRight: 'none',
    },
  },
};

const SCREEN_URL_PATHS: Record<string, string> = {
  picking: 'toplama',
  packing: 'paketleme',
  receiving: 'malkabul',
  cyclecount: 'stoksayim',
};

const SCREEN_LABELS: Record<WmsScreenType, string> = {
  picking: 'Toplama',
  packing: 'Paketleme',
  receiving: 'Mal Kabul',
  cyclecount: 'Stok Sayım',
};

const WmsScreenSetup: React.FC<WmsScreenSetupProps> = ({ screenType, title }) => {
  const { userDetails } = useAuth();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loading, setLoading] = useState(false);

  const [screenModalOpen, setScreenModalOpen] = useState(false);
  const [editScreen, setEditScreen] = useState<Screen | null>(null);
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [personnelScreen, setPersonnelScreen] = useState<{ id: string; name: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuScreen, setActionMenuScreen] = useState<Screen | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await smartWarehouseService.getAllList();
        setWarehouses(list.map((w) => ({ id: w.id, name: w.warehouseName })));
        if (list.length > 0) setSelectedWarehouse(list[0].id);
      } catch { setWarehouses([]); }
    })();
  }, []);

  const loadScreens = useCallback(async () => {
    if (!selectedWarehouse) return;
    setLoading(true);
    try {
      const res = await screenService.getScreens(screenType, selectedWarehouse);
      const rawScreens = (res.data?.data || res.data || []) as any[];
      const normalizedScreens: Screen[] = rawScreens.map((s) => ({
        id: String(s.id ?? s.Id ?? ''),
        name: s.name ?? s.Name ?? '',
        warehouseId: String(s.warehouseId ?? s.WarehouseId ?? selectedWarehouse),
        isActive: Boolean(s.isActive ?? s.IsActive),
        settings: s.settings ?? s.Settings,
        createdAt: s.createdAt ?? s.CreatedAt ?? '',
        cashierCount: Number(s.cashierCount ?? s.CashierCount ?? 0),
      }));

      const companyId = userDetails?.companyId;
      if (!companyId) {
        setScreens(normalizedScreens);
        return;
      }

      // Faz 3: ekran basina yetkili personel sayisi SQL'den (servis HybridFallback yonetir).
      const sqlPersonnel = await getPersonnelSql(companyId);
      const personnelCountByScreenId: Record<string, number> = {};
      const perPersonScreens = await Promise.all(
        (sqlPersonnel || []).map((p: any) =>
          wmsPersonnelPermissionService.getMyWmsScreens(companyId, p.id).catch(() => [] as string[])
        )
      );
      perPersonScreens.forEach((allowedScreens) => {
        allowedScreens.forEach((screenId) => {
          const key = String(screenId);
          personnelCountByScreenId[key] = (personnelCountByScreenId[key] || 0) + 1;
        });
      });

      const hydratedScreens = normalizedScreens.map((screen) => ({
        ...screen,
        cashierCount: personnelCountByScreenId[screen.id] ?? 0,
      }));
      setScreens(hydratedScreens);
    } catch { setScreens([]); }
    setLoading(false);
  }, [screenType, selectedWarehouse, userDetails?.companyId]);

  useEffect(() => { loadScreens(); }, [loadScreens]);

  const handleDelete = async (id: string) => {
    try {
      await screenService.deleteScreen(screenType, id);
      loadScreens();
    } catch { /* ignore */ }
    setDeleteConfirm(null);
  };

  const getScreenUrl = (id: string) =>
    `/#/wms/${SCREEN_URL_PATHS[screenType]}/${id}`;

  const selectedWarehouseName = warehouses.find(w => w.id === selectedWarehouse)?.name || 'Şube Atanmamış';
  const screenLabel = SCREEN_LABELS[screenType];

  const openActionMenu = (event: React.MouseEvent<HTMLElement>, screen: Screen) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuScreen(screen);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuScreen(null);
  };

  const handleOpenPersonnel = (screen: Screen) => {
    setPersonnelScreen({ id: screen.id, name: screen.name });
    setPersonnelModalOpen(true);
    closeActionMenu();
  };

  const handleOpenEdit = (screen: Screen) => {
    setEditScreen(screen);
    setScreenModalOpen(true);
    closeActionMenu();
  };

  const handleOpenDeleteConfirm = (screen: Screen) => {
    setDeleteConfirm(screen.id);
    closeActionMenu();
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
        <GuideButton guideKey="wms.screenSetup" />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Depo</InputLabel>
          <Select value={selectedWarehouse} label="Depo"
            onChange={e => setSelectedWarehouse(e.target.value)}>
            {warehouses.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => { setEditScreen(null); setScreenModalOpen(true); }}
          disabled={!selectedWarehouse}>
          Yeni {screenLabel} Ekranı
        </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : screens.length === 0 ? (
        <Alert severity="info">Bu depoda henüz ekran tanımlanmamış.</Alert>
      ) : (
        isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {screens.map((s, index) => (
              <Paper key={s.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {index + 1}. {s.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Depo:</Typography>
                  <Typography variant="body2">{selectedWarehouseName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.25 }}>
                  <Typography variant="body2" color="text.secondary">Personel:</Typography>
                  <Chip size="small" label={`${s.cashierCount} kişi`} variant="outlined" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    component="a"
                    href={getScreenUrl(s.id)}
                  >
                    Ekrana Git
                  </Button>
                  <IconButton size="small" onClick={(event) => openActionMenu(event, s)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={TABLE_HEADER_SX}>
                <TableRow>
                  <TableCell align="center" sx={{ width: 72 }}>Sıra</TableCell>
                  <TableCell>Ekran Adı</TableCell>
                  <TableCell>Depo</TableCell>
                  <TableCell align="center">Personel</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {screens.map((s, index) => (
                  <TableRow key={s.id} hover>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {selectedWarehouseName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${s.cashierCount} kişi`}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', justifyContent: 'flex-end', gap: 0.5, alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={getScreenUrl(s.id)}
                        >
                          Ekrana Git
                        </Button>
                        <IconButton size="small" onClick={(event) => openActionMenu(event, s)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )
      )}

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor && actionMenuScreen)}
        onClose={closeActionMenu}
      >
        <MuiMenuItem
          onClick={() => actionMenuScreen && handleOpenPersonnel(actionMenuScreen)}
        >
          <PeopleIcon fontSize="small" style={{ marginRight: 8 }} />
          Personel Yönetimi
          {actionMenuScreen ? (
            <Chip
              size="small"
              label={`${actionMenuScreen.cashierCount} kişi`}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          ) : null}
        </MuiMenuItem>
        <MuiMenuItem
          onClick={() => actionMenuScreen && handleOpenEdit(actionMenuScreen)}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Düzenle
        </MuiMenuItem>
        <MuiMenuItem
          onClick={() => actionMenuScreen && handleOpenDeleteConfirm(actionMenuScreen)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Sil
        </MuiMenuItem>
      </Menu>

      {deleteConfirm && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'error.main', color: 'white', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>Bu ekranı silmek istediğinize emin misiniz?</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" sx={{ color: 'white' }} onClick={() => setDeleteConfirm(null)}>İptal</Button>
            <Button size="small" variant="contained" color="warning" onClick={() => handleDelete(deleteConfirm)}>Sil</Button>
          </Box>
        </Box>
      )}

      <WmsScreenModal
        open={screenModalOpen}
        screenType={screenType}
        warehouseId={selectedWarehouse}
        editScreen={editScreen}
        onClose={() => { setScreenModalOpen(false); setEditScreen(null); }}
        onSaved={loadScreens}
      />

      {personnelScreen && (
        <WmsScreenPersonnelModal
          open={personnelModalOpen}
          screenId={personnelScreen.id}
          screenName={personnelScreen.name}
          onClose={() => { setPersonnelModalOpen(false); setPersonnelScreen(null); }}
          onChanged={loadScreens}
        />
      )}
    </Box>
  );
};

export default WmsScreenSetup;
